import { apiRequest } from '@scc/lib'
import type {
  LaunchProjectionRepository, CreateLaunchProjectionInput, FleetRow, FleetView, FleetViewInput, FleetInboxItem, FleetPerson,
} from '@/data/repositories/types'
import type {
  LaunchProjection, ProjectionOutputs, TemplateConfig,
  LaunchProjectionRevisionSummary, LaunchProjectionRevision,
  WizardConfig,
} from '@/modules/launch/templating-types'

const base = (agencyId: number, storeId: number) =>
  `/agencies/${agencyId}/stores/${storeId}/launch-projections`

export function createApiLaunchProjectionRepository(): LaunchProjectionRepository {
  return {
    // Agency-scoped
    compute: (agencyId, storeId, config: TemplateConfig) =>
      apiRequest<ProjectionOutputs>(`${base(agencyId, storeId)}/compute`, { method: 'POST', body: { config } }),
    create: (agencyId, storeId, input: CreateLaunchProjectionInput) =>
      apiRequest<LaunchProjection>(base(agencyId, storeId), { method: 'POST', body: input }),
    list: (agencyId, storeId) =>
      apiRequest<LaunchProjection[]>(base(agencyId, storeId)),
    get: (agencyId, storeId, id) =>
      apiRequest<LaunchProjection>(`${base(agencyId, storeId)}/${id}`),
    remove: async (agencyId, storeId, id) => {
      await apiRequest<void>(`${base(agencyId, storeId)}/${id}`, { method: 'DELETE' })
    },
    getFleet: (agencyId) => apiRequest<FleetRow[]>(`/agencies/${agencyId}/fleet`),
    listFleetViews: (agencyId) => apiRequest<FleetView[]>(`/agencies/${agencyId}/fleet/views`),
    createFleetView: (agencyId, input: FleetViewInput) =>
      apiRequest<FleetView>(`/agencies/${agencyId}/fleet/views`, { method: 'POST', body: input }),
    updateFleetView: (agencyId, id, input: Partial<FleetViewInput>) =>
      apiRequest<FleetView>(`/agencies/${agencyId}/fleet/views/${id}`, { method: 'PUT', body: input }),
    deleteFleetView: async (agencyId, id) => {
      await apiRequest<void>(`/agencies/${agencyId}/fleet/views/${id}`, { method: 'DELETE' })
    },
    getFleetInbox: (agencyId) => apiRequest<{ items: FleetInboxItem[]; unread: number }>(`/agencies/${agencyId}/fleet/inbox`),
    markFleetInboxRead: async (agencyId, ids) =>
      (await apiRequest<{ read: number }>(`/agencies/${agencyId}/fleet/inbox/read`, { method: 'POST', body: ids ? { ids } : {} })).read,
    runFleetWatch: (agencyId) =>
      apiRequest<{ accounts: number; events: number; notifications: number }>(`/agencies/${agencyId}/fleet/watch/run`, { method: 'POST', body: {} }),
    listFleetPeople: async (agencyId) => {
      // Admins and sales may list members; a strategist gets 403 and simply has no picker.
      try {
        const rows = await apiRequest<{ userId: string; email: string; displayName: string | null }[]>(`/agencies/${agencyId}/members`)
        return rows.map((m) => ({ userId: m.userId, name: m.displayName?.trim() || m.email }))
      } catch {
        return []
      }
    },
    getActive: (agencyId, storeId) =>
      apiRequest<LaunchProjection | null>(`${base(agencyId, storeId)}/active`),
    lock: (agencyId, storeId, id, startMonth) =>
      apiRequest<LaunchProjection>(`${base(agencyId, storeId)}/${id}/lock`, { method: 'POST', body: startMonth ? { startMonth } : {} }),
    unlock: (agencyId, storeId, id) =>
      apiRequest<LaunchProjection>(`${base(agencyId, storeId)}/${id}/unlock`, { method: 'POST' }),
    retire: (agencyId, storeId, id) =>
      apiRequest<LaunchProjection>(`${base(agencyId, storeId)}/${id}/retire`, { method: 'POST' }),
    setStartMonth: (agencyId, storeId, id, startMonth) =>
      apiRequest<LaunchProjection>(`${base(agencyId, storeId)}/${id}/start-month`, { method: 'POST', body: { startMonth } }),
    // Revisions — agency-scoped
    listRevisions: (agencyId, storeId, id) =>
      apiRequest<LaunchProjectionRevisionSummary[]>(`${base(agencyId, storeId)}/${id}/revisions`),
    getRevision: (agencyId, storeId, id, rev) =>
      apiRequest<LaunchProjectionRevision>(`${base(agencyId, storeId)}/${id}/revisions/${rev}`),
    // Amend — agency-scoped
    amend: (agencyId, storeId, id, config, note, lockedMonths) =>
      apiRequest<LaunchProjection>(`${base(agencyId, storeId)}/${id}/amend`, {
        method: 'POST',
        body: {
          config,
          ...(note !== undefined ? { note } : {}),
          ...(lockedMonths !== undefined ? { lockedMonths } : {}),
        },
      }),
    // Launch Wizard — agency-scoped
    createFromWizard: (agencyId, storeId, name: string, wizard: WizardConfig, kind) =>
      apiRequest<LaunchProjection>(`${base(agencyId, storeId)}/from-wizard`, { method: 'POST', body: { name, wizard, kind } }),
    updateFromWizard: (agencyId, storeId, id: string, name: string, wizard: WizardConfig) =>
      apiRequest<LaunchProjection>(`${base(agencyId, storeId)}/${id}/from-wizard`, { method: 'PUT', body: { name, wizard } }),
    // Overwrite a draft's full config (3-channel builder edit path)
    updateConfig: (agencyId, storeId, id: string, name: string, config: TemplateConfig, kind) =>
      apiRequest<LaunchProjection>(`${base(agencyId, storeId)}/${id}/config`, { method: 'PUT', body: kind ? { name, config, kind } : { name, config } }),
    // Snapshot sharing — mint / revoke the public share token
    enableShare: (agencyId, storeId, id: string, expiresAt?: Date | null) =>
      apiRequest<{ shareToken: string }>(`${base(agencyId, storeId)}/${id}/share`, {
        method: 'POST',
        body: expiresAt === undefined
          ? {}
          : { expiresAt: expiresAt === null ? null : expiresAt.toISOString() },
      }),
    disableShare: async (agencyId, storeId, id: string) => {
      await apiRequest<void>(`${base(agencyId, storeId)}/${id}/share`, { method: 'DELETE' })
    },
  }
}
